import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaSearch, FaMoneyBillWave, FaCalendarAlt, FaReceipt, FaComment, FaStore } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import './CreditRegister.css';

const API_URL = 'https://dankula.x10.mx/auth.php';
const bankOptions = ['CBE', 'Awash', 'Dashen', 'Abyssinia', 'Birhan', 'Telebirr', 'Check'];

const CreditRegister = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [comment, setComment] = useState('');
  const [customer, setCustomer] = useState('');
  const [unpaidAmount, setUnpaidAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [networkError, setNetworkError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [dailySales, setDailySales] = useState([]);
  const [showDailySales, setShowDailySales] = useState(false);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setNetworkError(false);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(
        API_URL,
        {},
        {
          params: { action: 'getProducts' },
          headers: headers,
        }
      );

      const availableProducts = response.data.products.filter(
        (product) => product.status === 'in_store' && product.quantity > 0
      );
      
      setProducts(availableProducts);
    } catch (err) {
      console.error("Fetch products error:", err);
      setNetworkError(true);
      toast.error('Failed to fetch products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailySales = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API_URL,
        {},
        {
          params: { action: 'get_daily_sales2' },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setDailySales(response.data.sales);
      } else {
        toast.error(response.data.message || 'Failed to fetch daily sales.');
      }
    } catch (error) {
      console.error('Fetch daily sales error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred while fetching daily sales.';
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchDailySales();
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      const uniqueCategories = ['All', ...new Set(products.map(p => p.category))];
      setCategories(uniqueCategories);
    }
  }, [products]);

  // Cart management functions
  const addToCart = (product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product_id === product.id);
      if (existingItem) {
        return prevCart.map(item => 
          item.product_id === product.id 
            ? {...item, quantity: item.quantity + 1} 
            : item
        );
      }
      return [...prevCart, {
        product_id: product.id,
        name: product.name,
        quantity: 1,
        price: product.selling_price,
        maxQuantity: product.quantity
      }];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
  };

  const updateCartItem = (productId, field, value) => {
    setCart(prevCart => 
      prevCart.map(item => 
        item.product_id === productId ? {...item, [field]: value} : item
      )
    );
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => 
      total + (item.quantity * item.price), 0
    ).toFixed(2);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!customer) {
      toast.error('Please enter customer name');
      return;
    }

    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const saleData = {
        cart: JSON.stringify(cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          discount: item.discount || 0,
        }))),
        comment,
        payment_method: 'credit',
        customer_name: customer,
        unpaid_amount: parseFloat(unpaidAmount) || 0,
        action: 'checkout2',
      };

      console.log('Sending sale data:', saleData);
      const response = await axios.post(
        API_URL,
        saleData,
        { headers: headers }
      );
      console.log('Backend response:', response.data);

      if (response.data.success) {
        toast.success('Checkout completed successfully');
        setCart([]);
        setComment('');
        setCustomer('');
        setUnpaidAmount('');
        fetchProducts();
      } else {
        toast.error(response.data.message || 'Checkout failed');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.message || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  const deleteSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API_URL,
        { sale_id: saleId },
        {
          params: { action: 'delete_sale' },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        toast.success('Sale deleted successfully');
        fetchDailySales();
      } else {
        toast.error(response.data.message || 'Failed to delete sale');
      }
    } catch (error) {
      console.error('Delete sale error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred while deleting the sale.';
      toast.error(errorMessage);
    }
  };

  const getFilteredProducts = () => {
    let filtered = [...products];
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(query) ||
        (product.description && product.description.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };

  return (
    <div className="credit-register-container">
      <button 
        className="toggle-button"
        onClick={() => setShowDailySales(!showDailySales)}
      >
        {showDailySales ? 'Hide' : 'Show'} Daily Sales
      </button>

      {showDailySales && (
        <div className="daily-sales-container">
          <h3 className="section-title">Today's Sales</h3>
          <div className="sales-list">
            {dailySales.length > 0 ? (
              dailySales.map(sale => (
                <div key={sale.transaction_item_id} className="sale-item">
                  <div className="sale-info">
                    <span className="sale-product">{sale.product_name}</span>
                    <span className="sale-details">
                      Qty: {sale.quantity} | Price: {sale.price} | Total: {sale.total_amount}
                    </span>
                  </div>
                  <button 
                    className="delete-button"
                    onClick={() => deleteSale(sale.transaction_item_id)}
                  >
                    Delete
                  </button>
                </div>
              ))
            ) : (
              <p>No sales recorded for today.</p>
            )}
          </div>
        </div>
      )}

      <div className="search-row">
        <input
          type="text"
          className="search-input"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button 
          className="search-button"
          onClick={fetchProducts}
        >
          <FaSearch />
        </button>
      </div>

      <div className="category-scroll">
        {categories.map(category => (
          <button
            key={category}
            className={`category-button ${selectedCategory === category ? 'selected' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="main-content">
        <div className="cart-section">
          <div className="section-header">
            <h3 className="section-title">Current Sale</h3>
            <span className="item-count">{cart.length} {cart.length === 1 ? 'item' : 'items'}</span>
          </div>
          
          {cart.length === 0 ? (
            <div className="empty-cart">
              <FaShoppingCart className="cart-icon" />
              <p>Your cart is empty</p>
            </div>
          ) : (
            <div className="cart-items-container">
              {cart.map(item => (
                <div key={item.product_id} className="cart-item">
                  <div className="cart-item-info">
                    <h4 className="cart-item-name">{item.name}</h4>
                    <input
                      type="number"
                      className="cart-item-price"
                      value={item.price}
                      onChange={(e) => {
                        const num = parseFloat(e.target.value) || 0;
                        updateCartItem(item.product_id, 'price', Math.max(0, num));
                      }}
                    />
                  </div>
                  <div className="cart-item-controls">
                    <button 
                      className="quantity-button"
                      onClick={() => updateCartItem(item.product_id, 'quantity', Math.max(1, item.quantity - 1))}
                    >
                      <FaMinus />
                    </button>
                    <input
                      type="number"
                      className="quantity-input"
                      value={item.quantity}
                      onChange={(e) => {
                        const num = parseInt(e.target.value) || 1;
                        updateCartItem(item.product_id, 'quantity', Math.min(num, item.maxQuantity));
                      }}
                    />
                    <button 
                      className="quantity-button"
                      onClick={() => updateCartItem(item.product_id, 'quantity', Math.min(item.quantity + 1, item.maxQuantity))}
                    >
                      <FaPlus />
                    </button>
                    <button 
                      className="remove-button"
                      onClick={() => removeFromCart(item.product_id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
              <div className="total-container">
                <span className="total-label">Total:</span>
                <span className="total-amount">ETB {calculateTotal()}</span>
              </div>
            </div>
          )}
        </div>

        <div className="customer-info">
          <h3 className="section-title">Customer Information</h3>
          <div className="form-group">
            <label>Customer Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter customer name"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Unpaid Amount</label>
            <input
              type="number"
              className="form-input"
              placeholder="Enter unpaid amount"
              value={unpaidAmount}
              onChange={(e) => setUnpaidAmount(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Comment</label>
            <textarea
              className="form-textarea"
              placeholder="Add a comment (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="3"
            />
          </div>
        </div>

        <div className="products-section">
          <h3 className="section-title">Available Products</h3>
          {loading ? (
            <div className="loading">Loading...</div>
          ) : networkError ? (
            <div className="error-message">Network Error. Please try again.</div>
          ) : (
            <div className="product-grid">
              {getFilteredProducts().map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-content">
                    <h4 className="product-name">{product.name}</h4>
                    <p className="product-price">{product.selling_price} ETB</p>
                    <p className="product-stock">Available: {product.quantity}</p>
                  </div>
                  <button 
                    className="add-to-cart-button"
                    onClick={() => addToCart(product)}
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="footer-actions">
        <button 
          className="clear-button"
          onClick={() => setCart([])}
          disabled={cart.length === 0}
        >
          <FaTrash /> Clear All
        </button>
        <button 
          className={`checkout-button ${processing || cart.length === 0 ? 'disabled' : ''}`}
          onClick={handleCheckout}
          disabled={processing || cart.length === 0}
        >
          {processing ? 'Processing...' : (
            <>
              <FaShoppingCart /> Checkout
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CreditRegister;
